import PageNotFound from "@/shared/404/pages/page-not-found.vue";
import { useHasAccessAsync } from "@/shared/access-control/composables/use-has-access-async.ts";
import { Tenant } from "@/shared/composables/tenant";
import { createAbilityList } from "@/shared/environment/ability-list";
import { useAbilityRoute } from "@/shared/environment/ability-route";
import { Ability } from "@/shared/environment/ability.types";
import { useAbilityStore } from "@/shared/store/ability.store";
import { RouteRecordRaw, Router } from "vue-router";

/**
 * Filters the full ability list to only those the current user has access to.
 */
export const filterAccessibleAbilities = async (): Promise<Ability[]> => {
    const abilities: Ability[] = [];

    for (const ability of createAbilityList()) {
        if (ability.access) {
            const hasAccess = await useHasAccessAsync({
                resource: ability.access.accessResource,
                featureID: ability.access.accessFeature,
                action: ability.access.accessAction,
                ignoreConditions: true,
            });
            if (hasAccess) abilities.push(ability);
        } else {
            abilities.push(ability);
        }
    }

    return abilities;
};

/**
 * Runs each ability's optional init() hook and returns only those that succeeded.
 */
export const initializeAbilities = async (accessibleAbilities: Ability[]): Promise<Ability[]> => {
    const abilitiesInitFailed: Ability[] = [];

    for (const ability of accessibleAbilities) {
        if (ability.init) {
            const initSuccessful = await ability.init();
            if (!initSuccessful) abilitiesInitFailed.push(ability);
        }
    }

    return accessibleAbilities.filter((ability) => !abilitiesInitFailed.includes(ability));
};

/**
 * Builds Vue Router routes from abilities and adds catch-all / redirect routes.
 */
export const buildAbilityRoutes = async (abilities: Ability[], tenant: Tenant): Promise<RouteRecordRaw[]> => {
    const { createRoutesFromAbilities } = useAbilityRoute();
    const routes = await createRoutesFromAbilities(abilities);

    if (routes.length > 0) {
        const firstRoute = tenant.landingPage ?? routes[0].path;
        routes.push(
            {
                path: "/",
                redirect: firstRoute,
            },
            {
                path: "/recovery",
                redirect: firstRoute,
            },
            {
                path: "/:pathMatch(.*)*",
                component: PageNotFound,
            }
        );
    }

    return routes;
};

/**
 * Registers the given routes with the Vue Router instance.
 */
export const registerRoutes = (router: Router, routes: RouteRecordRaw[]): void => {
    for (const route of routes) {
        router.addRoute({ ...route });
    }
};

/**
 * Orchestrates ability initialisation: filters by access, runs init hooks,
 * builds & registers routes, and stores the active abilities.
 */
export const initAbilities = async (router: Router, tenant: Tenant) => {
    const accessibleAbilities = await filterAccessibleAbilities();
    const abilities = await initializeAbilities(accessibleAbilities);

    const routes = await buildAbilityRoutes(abilities, tenant);
    registerRoutes(router, routes);

    const abilityStore = useAbilityStore();
    abilityStore.setAbilities(abilities);

    const defaultPath = tenant.landingPage ?? routes[0].path;
    return { defaultPath };
};
